import { useQuery } from "@tanstack/react-query";
import { TenantMark } from "@/components/shell/project-switcher";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { ApiError, currentUserQuery } from "@/lib/api";
import type { MembershipProject } from "@zworks/console-contracts";

/**
 * The caller's account details: name, role in this project, and project name. Read-only; there is
 * no profile update endpoint, so nothing here can be edited.
 */
export function ProfileSection({ project }: { project: MembershipProject }) {
  const me = useQuery(currentUserQuery);

  if (me.isPending) return <LoadingState label="Loading profile" rows={2} />;
  if (me.isError) {
    return (
      <ErrorState
        title="Profile could not be loaded"
        description={
          me.error instanceof ApiError
            ? (me.error.problem?.detail ?? me.error.message)
            : "The request could not be completed."
        }
        requestId={me.error instanceof ApiError ? (me.error.requestId ?? "unknown") : "unknown"}
        onRetry={() => void me.refetch()}
      />
    );
  }

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <section aria-label="Profile" className="flex items-center gap-4 rounded-lg border p-4">
        <TenantMark name={me.data.displayName} id={me.data.id} className="size-12 text-base" />
        <div className="min-w-0">
          <p className="truncate text-lg font-semibold tracking-tight">{me.data.displayName}</p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {project.role === "owner" ? "Owner" : project.role} · {project.name}
          </p>
        </div>
      </section>
      <EmptyState
        title="Profile editing is not available yet"
        description="Your name comes from your identity provider. Editing it here is not connected yet."
      />
    </div>
  );
}
